import { FC } from 'react';
import cx from 'classnames';
import { AmlHistoryItemData } from '@/redux/models';
import { AmlFlowEnum, AmlStatusEnum, RiskScoreLevelEnum } from '@/api';
import {
  getDisplayAmlFlow,
  getFormattedDateWithTime,
  getIconByNetwork,
  getShortAddress,
  getShortHash,
  isFlowLimited,
  isFlowLite,
} from '@/helpers';
import { ChainsDictionary } from '@/config';
import {
  BlacklistBadge,
  Button,
  CopyText,
  MonitoringSubscribe,
  RiskBadge,
  RiskBadgeBar,
  TableFigure,
} from '@/components';
import { useTranslation } from 'react-i18next';
import type { MenuProps } from 'antd';
import { Dropdown, Popover, Space } from 'antd';
import { MoreOutlined } from '@ant-design/icons';

import styles from './HistoryItem.module.sass';

interface ActionMenuProps {
  canDownloadPDFReport?: boolean;
  loading: boolean;
  onCheck: () => void;
  onDownload: () => void;
  monitoringAllowed: boolean;
  monitoringBonuses?: number;
  item: AmlHistoryItemData;
}

const ActionMenu: FC<ActionMenuProps> = (props) => {
  const {
    canDownloadPDFReport,
    loading,
    onCheck,
    onDownload,
    monitoringAllowed,
    monitoringBonuses,
    item,
  } = props;
  const { t } = useTranslation();

  const isInstantFlow = item.flow === AmlFlowEnum.INSTANT;

  const menuItems: MenuProps['items'] = [
    {
      key: 'onCheck',
      label: (
        <button type="button" onClick={onCheck} disabled={loading}>
          {t('translation.action_recheck_btn')}
        </button>
      ),
    },
    ...(!canDownloadPDFReport
      ? []
      : [
          {
            key: 'onDownload',
            label: (
              <button type="button" onClick={onDownload} disabled={loading}>
                {t('translation.download_pdf_btn')}
              </button>
            ),
          },
        ]),

    ...(monitoringAllowed && !isFlowLimited(item.flow) && !isInstantFlow
      ? [
          {
            key: 'onSubscribe',
            label: (
              <MonitoringSubscribe item={item}>
                {(openConfirmModal: any) => (
                  <Popover
                    zIndex={9999}
                    content={t('translation.monitoring_bonus_left', {
                      count: monitoringBonuses,
                    })}
                  >
                    <button
                      className={styles.monitor}
                      type="button"
                      disabled={loading}
                      onClick={openConfirmModal}
                    >
                      {t('translation.subscribe_btn')}{' '}
                      <span className={styles.monitor__count}>
                        {monitoringBonuses}
                      </span>
                    </button>
                  </Popover>
                )}
              </MonitoringSubscribe>
            ),
          },
        ]
      : []),
  ];

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      placement="top"
      disabled={loading}
      arrow={{ pointAtCenter: true }}
    >
      <Space>
        <MoreOutlined
          rotate={90}
          className={`${styles.icon} ${styles.icon_action} ${
            loading ? styles.icon_action_disabled : ''
          }`}
        />
      </Space>
    </Dropdown>
  );
};

interface HistoryAmlItemProps {
  canDownloadPDFReport?: boolean;
  item: AmlHistoryItemData;
  onCheck: (uuid: string) => void;
  onDetails: (item: AmlHistoryItemData) => void;
  loading: boolean;
  monitoringBonuses?: number;
}

const HistoryAmlItem: FC<HistoryAmlItemProps> = (props) => {
  const {
    canDownloadPDFReport,
    item,
    onCheck,
    onDetails,
    loading,
    monitoringBonuses,
  } = props;
  const { t } = useTranslation();

  const isPending = item.status === AmlStatusEnum.PENDING;
  const monitoringAllowed = Boolean(
    !isPending && !item.tx && monitoringBonuses && monitoringBonuses > 0,
  );

  const { uuid } = item;
  const networkIcon = getIconByNetwork(item.network);
  const shortAddress = !!item.tx
    ? getShortHash(item.tx)
    : getShortAddress(item.address);
  const lastChecked = getFormattedDateWithTime(item.updated_at);
  const hasRiskScore = item.riskscore !== null;
  const riskValue = item?.riskscore || 0;
  const riskScoreLevel = item.riskScoreLevel || null;
  const blacklistFlag = item.hasBlackListFlag;
  const networkName = ChainsDictionary[item.network]?.name || item.network;
  const assetSymbol = item.tokenDetails?.code || item.asset;
  const isLite = isFlowLite(item.flow);

  const detailsHandler = () => onDetails(item);

  const checkHandler = () => onCheck(uuid);

  const downloadHandler = () => {
    window.open(item.pdfReport, '_blank');
  };

  const rowClasses = cx(styles.entry, {
    [styles.row]: true,
    [`${styles['is-pending']}`]: isPending,
  });

  const isTxn = !!item.tx;
  const hasAmount = !!item.amount;

  return (
    <div className={rowClasses}>
      <div className={`${styles.col} ${styles['col--icon']}`}>
        <img src={networkIcon} alt={item.network} />
      </div>
      <div className={`${styles.col} ${styles['col--name']}`}>
        <div className={styles.crypto}>
          <div className={styles.crypto__address}>
            <CopyText text={shortAddress} copyText={item.tx || item.address} />
          </div>
          <div className={styles.crypto__info}>
            <span className={styles.crypto__network}>{networkName}</span>
            {isTxn && !isPending && (
              <span className={styles.crypto__asset}>{assetSymbol}</span>
            )}
          </div>
        </div>
      </div>
      <div className={`${styles.col} ${styles['col--direction']}`}>
        {isTxn && hasAmount && !isPending && (
          <TableFigure
            figure={
              !!item.amountData?.amount
                ? `${item.amountData?.amount} ${item.amountData?.asset}`
                : ''
            }
            note={(item.direction || '').toUpperCase()}
          />
        )}
      </div>
      <div className={`${styles.col} ${styles['col--risky']}`}>
        {isTxn && !isPending && (
          <TableFigure
            note={
              item.amountData?.hasRiskyAmount
                ? (item.amountData?.riskyUsd || '').toUpperCase()
                : null
            }
            figure={
              item.amountData?.hasRiskyAmount
                ? `${item.amountData?.riskyAmount} ${item.amountData?.asset}`
                : ''
            }
          />
        )}
      </div>
      <div className={`${styles.col} ${styles['col--status']}`}>
        {isPending && <span>{t('translation.status_pending_short')}</span>}
        {!isPending && (
          <>
            {hasRiskScore && !blacklistFlag && (
              <RiskBadge
                value={riskValue}
                note={
                  <span>
                    <b>{lastChecked}</b>
                  </span>
                }
              />
            )}

            {riskScoreLevel && !blacklistFlag && (
              <>
                {isLite && (
                  <RiskBadgeBar
                    level={riskScoreLevel}
                    note={
                      <span>
                        <b>{lastChecked}</b>
                      </span>
                    }
                  />
                )}
                {!isLite && (
                  <RiskBadge
                    value={50}
                    comparison={
                      riskScoreLevel === RiskScoreLevelEnum.HIGH ? '>' : '<'
                    }
                    note={
                      <span>
                        <b>{lastChecked}</b>
                      </span>
                    }
                  />
                )}
              </>
            )}

            {!hasRiskScore &&
              !blacklistFlag &&
              !riskScoreLevel &&
              t('translation.tpl_aml_no_score')}
          </>
        )}
      </div>
      <div className={`${styles.col} ${styles['col--label']}`}>
        {blacklistFlag ? <BlacklistBadge flag={blacklistFlag} /> : 'n/a'}
      </div>
      <div className={`${styles.col} ${styles['col--method']}`}>
        {getDisplayAmlFlow(item.flow)}
      </div>
      <div className={`${styles.col} ${styles['col--actions']}`}>
        <div className={styles.col}>
          <Button
            size="m"
            type="secondary"
            onClick={detailsHandler}
            disabled={loading}
            nowrap
          >
            {t('translation.details')}
          </Button>
        </div>
        {!isPending && (
          <div className={`${styles.col} ${styles['col--dots']}`}>
            <ActionMenu
              canDownloadPDFReport={canDownloadPDFReport}
              loading={loading}
              onCheck={checkHandler}
              onDownload={downloadHandler}
              monitoringAllowed={monitoringAllowed}
              monitoringBonuses={monitoringBonuses}
              item={item}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryAmlItem;
